import {useAccessToken} from "../../services/auth"
import React, {useCallback, useEffect, useState} from "react"
import {JWTProviders} from "../../resources/orgSettings"
import Auth0Fields from "../../components/JwtProviders/readyToLaunch/Auth0Fields"
import StytchFileds from "../../components/JwtProviders/readyToLaunch/StytchFileds"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useToasts} from "react-toast-notifications"
import CognitoFields from "../../components/JwtProviders/readyToLaunch/CognitoFields"
import OktaFields from "../../components/JwtProviders/readyToLaunch/OktaFields"
import {AppConfig, updateAppConfig} from "../../resources/whiteLabel"
import CustomFields from "../../components/JwtProviders/readyToLaunch/CustomFields"
import Button from "../../components/Button/Button"

export default function Authentication({appConfig, refresh}: {appConfig: AppConfig; refresh: () => void}) {
	const accessToken = useAccessToken()
	const [state, patch] = useAsyncResultIdle(updateAppConfig)
	const [provider, setProvider] = useState<JWTProviders>(
		appConfig?.attributes?.jwtProvider?.provider ?? JWTProviders.Custom
	)
	const [jwksPath, setJwksPath] = useState<string>(appConfig?.attributes?.jwtProvider?.jwksPath ?? "")
	const [jwtIssuer, setJwtIssuer] = useState<string | undefined>(appConfig?.attributes?.jwtIssuer ?? undefined)
	const [jwtPublicKey, setJwtPublicKey] = useState<string>(appConfig?.attributes?.jwtPublicKey ?? "")
	const {addToast} = useToasts()

	useEffect(() => {
		if (state.isOk()) {
			addToast("Org Settings Updated Successfully", {appearance: "success"})
			refresh()
		}
	}, [state])

	const updateField = useCallback((fieldName: string, value: string) => {
		switch (fieldName) {
			case "jwksPath":
				setJwksPath(value)
				break
			case "jwtIssuer":
				setJwtIssuer(value)
				break
			case "jwtPublicKey":
				setJwtPublicKey(value)
				break
		}
	}, [])

	const onProviderChange = useCallback((selectedProvider: JWTProviders) => {
		let jwksPathInitializer: string | undefined = ""
		let jwtPublicKeyInitializer: string | undefined = ""
		let issuerInitializer: string | undefined = undefined
		if (appConfig?.attributes?.jwtProvider?.provider == selectedProvider) {
			jwksPathInitializer = appConfig?.attributes?.jwtProvider?.jwksPath ?? ""
			issuerInitializer = appConfig?.attributes?.jwtIssuer
		} else if (selectedProvider == JWTProviders.Custom && appConfig?.attributes?.jwtProvider?.provider == null) {
			issuerInitializer = appConfig?.attributes?.jwtIssuer
			jwtPublicKeyInitializer = appConfig?.attributes?.jwtPublicKey ?? ""
		}
		setProvider(selectedProvider)
		setJwksPath(jwksPathInitializer as string)
		setJwtIssuer(issuerInitializer)
		setJwtPublicKey(jwtPublicKeyInitializer as string)
	}, [])

	const getProviderFields = useCallback(() => {
		switch (provider) {
			case JWTProviders.Auth0:
				return (
					<Auth0Fields jwksPath={jwksPath} jwtIssuer={jwtIssuer} updateSettings={updateField} provider={provider} />
				)
			case JWTProviders.Stytch:
				return (
					<StytchFileds jwksPath={jwksPath} jwtIssuer={jwtIssuer} updateSettings={updateField} provider={provider} />
				)
			case JWTProviders.Cognito:
				return <CognitoFields jwksPath={jwksPath} updateSettings={updateField} provider={provider} />
			case JWTProviders.Okta:
				return <OktaFields jwksPath={jwksPath} jwtIssuer={jwtIssuer} updateSettings={updateField} provider={provider} />
			case JWTProviders.Custom:
				return <CustomFields jwtPublicKey={jwtPublicKey} jwtIssuer={jwtIssuer} updateSettings={updateField} />
			default:
				return null
		}
	}, [provider, jwksPath, jwtIssuer, jwtPublicKey])

	return (
		<div className="card ready-to-launch-settings-card">
			<div className={"authentication-content card-content"}>
				<div className="description">
					<p>To authenticate users, this White-Label App requires a JWT token passed via the jwt-token attribute.</p>
					We support tokens from Okta, Auth0, AWS Cognito, and Stytch—just configure the JWKS Path and JWT Issuer. Using
					a different provider or custom tokens? Choose Custom and provide the public key for validation.
				</div>
				<label className="label">Identity Provider</label>
				<div className="select">
					<select value={provider} onChange={(e) => onProviderChange(e.target.value as JWTProviders)}>
						<option value={JWTProviders.Auth0}>Auth0</option>
						<option value={JWTProviders.Stytch}>Stytch</option>
						<option value={JWTProviders.Cognito}>Amazon Cognito</option>
						<option value={JWTProviders.Okta}>Okta</option>
						<option value={JWTProviders.Custom}>Custom</option>
					</select>
				</div>
				<form
					onSubmit={(e) => {
						e.preventDefault()
						patch(accessToken, {
							jwtProvider: {
								provider: provider === JWTProviders.Custom ? null : provider,
								jwksPath: jwksPath === "" ? null : jwksPath,
							},
							jwtIssuer,
							jwtPublicKey: provider === JWTProviders.Custom ? jwtPublicKey : null,
						})
					}}
				>
					{getProviderFields()}
					<div className="columns">
						<div className="column mt-4 button-container">
							{state.match(
								() => (
									<Button className="button primary" type="submit" prefixIcon="approve-v-check-validation">
										Save
									</Button>
								),
								() => (
									<Button className="button primary is-loading">Saving</Button>
								),
								(_) => null,
								(err) => (
									<>
										<div className="has-text-danger">{err.errors[0].title}</div>
										<Button className="button primary" type="submit" prefixIcon="approve-v-check-validation">
											Save
										</Button>
									</>
								)
							)}
						</div>
					</div>
				</form>
			</div>
		</div>
	)
}
